
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Layout } from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useGroupId } from "@/hooks/useGroupId";
import { useTranslation } from "react-i18next";
import { InviteErrorView } from "@/components/invite/InviteErrorView";
import { InviteLoadingView } from "@/components/invite/InviteLoadingView";
import { InviteAuthView } from "@/components/invite/InviteAuthView";
import { InviteGroupView } from "@/components/invite/InviteGroupView";

const GroupInvitePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [groupName, setGroupName] = useState<string>("");
  const [memberCount, setMemberCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [isJoining, setIsJoining] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAuthForm, setShowAuthForm] = useState<boolean>(false);
  const [authMode, setAuthMode] = useState<"signup" | "login">("signup");
  
  const groupId = useGroupId();
  
  console.log('GroupInvitePage rendered with:', {
    groupId,
    fullUrl: window.location.href,
    hostname: window.location.hostname,
    search: window.location.search,
    inviteParams: new URLSearchParams(window.location.search).get('id')
  });
  
  useEffect(() => {
    const fetchGroupDetails = async () => {
      try {
        console.log('Fetching group details for ID:', groupId);
        
        if (!groupId) {
          console.error('No group ID found in URL');
          setError(t('groupInvite.error'));
          setLoading(false);
          return;
        }
        
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('name, status')
          .eq('id', groupId)
          .single();
        
        if (groupError) {
          console.error('Error fetching group:', groupError);
          setError(t('groupInvite.failedToJoin'));
          setLoading(false);
          return;
        }
        
        console.log('Group data:', groupData);
        
        if (groupData?.status === 'archived') {
          setError(t('groups.archiveGroupInfo'));
          setLoading(false);
          return;
        }

        if (!groupData) {
          console.error('No group found with ID:', groupId);
          setError(t('groupInvite.error'));
          setLoading(false);
          return;
        }
        
        const { data: membersData, error: membersError } = await supabase
          .from('group_members')
          .select('id')
          .eq('group_id', groupId);
        
        if (membersError) {
          console.error('Error fetching members:', membersError);
        } else {
          setMemberCount(membersData?.length || 0);
        }
        
        setGroupName(groupData?.name || t('groupInvite.unknownGroup'));
        setLoading(false);
      } catch (err) {
        console.error('Unexpected error:', err);
        setError(t('groupInvite.unexpectedError'));
        setLoading(false);
      }
    };
    
    fetchGroupDetails();
  }, [groupId, t]);

  const handleSubmitAuth = (e: React.FormEvent) => {
    console.log('GroupInvitePage - handleSubmitAuth called, storing pendingInvite:', groupId);
    
    // Store the group ID in localStorage for post-auth processing
    if (groupId) {
      localStorage.setItem('pendingInvite', groupId);
    }
  };
  
  const handleJoinGroup = async () => {
    try {
      setIsJoining(true);
      
      if (!groupId) {
        console.error('Attempting to join group without group ID');
        toast({
          title: t('common.error'),
          description: t('groupInvite.error'),
          variant: "destructive"
        });
        setIsJoining(false);
        return;
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.log('No session found, showing auth form');
        setShowAuthForm(true);
        setIsJoining(false);
        return;
      }

      console.log('Checking if user is already a member');
      const { data: existingMember, error: checkError } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', groupId)
        .eq('profile_id', session.user.id)
        .maybeSingle();
      
      if (checkError) {
        console.error('Error checking membership:', checkError);
        throw checkError;
      }
      
      if (existingMember) {
        console.log('User is already a member, redirecting to group');
        toast({
          description: t('groupInvite.alreadyMember'),
          className: "bg-gradient-to-r from-primary to-green text-white"
        });
        navigate(`/group/${groupId}`);
        return;
      }
      
      console.log('Adding user to group');
      const { error } = await supabase
        .from('group_members')
        .insert([
          {
            group_id: groupId,
            profile_id: session.user.id,
            is_admin: false
          }
        ]);
      
      if (error) {
        console.error('Error joining group:', error);
        throw error;
      }
      
      console.log('Successfully joined group');
      toast({
        description: t('groupInvite.successJoined'),
        className: "bg-gradient-to-r from-primary to-green text-white"
      });
      
      navigate(`/group/${groupId}`);
    } catch (error) {
      console.error('Error joining group:', error);
      toast({
        title: t('common.error'),
        description: t('groupInvite.failedToJoin'),
        variant: "destructive"
      });
    } finally {
      setIsJoining(false);
    }
  };

  const switchToSignUp = () => setAuthMode("signup");
  const switchToLogin = () => setAuthMode("login");
  
  const backToInvite = () => setShowAuthForm(false);
  
  if (error) {
    return (
      <Layout>
        <InviteErrorView error={error} />
      </Layout>
    );
  }
  
  if (showAuthForm) {
    return (
      <Layout>
        <InviteAuthView
          authMode={authMode}
          groupName={groupName}
          groupId={groupId || ''} // Pass the groupId to InviteAuthView
          onBackToInvite={backToInvite}
          onSubmitAuth={handleSubmitAuth}
          onSwitchToLogin={switchToLogin}
          onSwitchToSignUp={switchToSignUp}
        />
      </Layout>
    );
  }
  
  return (
    <Layout>
      {loading ? (
        <InviteLoadingView />
      ) : (
        <InviteGroupView
          groupName={groupName}
          memberCount={memberCount}
          isJoining={isJoining}
          onJoinGroup={handleJoinGroup}
          onCancel={() => navigate('/')}
        />
      )}
    </Layout>
  );
};

export default GroupInvitePage;
